/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useCallback, useEffect } from "react";
import Loader from "react-loader";
import {
  Typography,
  Grid,
  Paper,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import moment from "moment";
// components
import MainLayout from "../../components/hoc/MainLayout";
import DateFilter from "../../components/DateFilter";
import Export from "../../components/reports/ExportFYF";
// helpers
import { apiGet } from "../../helpers/api";
import displayDuration from "../../helpers/displayDuration";
import displayAmount from "../../helpers/displayAmount";

const useStyles = makeStyles((theme) => ({
  table: { tableLayout: "fixed" },
  noResults: { fontStyle: "italic", color: "#b99d9d" },
  root: {
    flexGrow: 1,
  },
  paper: {
    padding: theme.spacing(2),
    textAlign: "center",
    color: theme.palette.text.secondary,
  },
  storeHeader: {
    backgroundColor: "#666",
  },
  riderHeader: {
    textAlign: "center",
    fontWeight: "bold",
    padding: "5px",
  },
  header: {
    color: "#fff",
    fontWeight: "bold",
  },
  grandtotal: {
    fontWeight: "bold",
  },
}));

function FYFReport() {
  const classes = useStyles();

  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(new Date());
  const [data, setData] = useState(null);

  const fetchData = useCallback(async () => {
    let start = moment(startDate).format("YYYY-MM-DD");
    let end = moment(endDate).format("YYYY-MM-DD");

    // get data from api
    try {
      setData(null);

      let url = `/reports/deliveries/fyf?date_from=${start}&date_to=${end}`;
      const response = await apiGet(url);

      setData(response.data);
    } catch (e) {
      console.log(e.response?.data?.message);
    }
  }, [startDate, endDate]);

  useEffect(() => {
    fetchData();
  }, []);

  const renderTableHeader = () => {
    return (
      <TableHead>
        <TableRow>
          <TableCell component="th" scope="row">
            Store
          </TableCell>
          <TableCell>ODT</TableCell>
          <TableCell>ADT</TableCell>
          <TableCell>TC</TableCell>
        </TableRow>
      </TableHead>
    );
  };

  const renderStores = () => {
    let total = 0;
    return (
      <TableContainer component={Paper}>
        <Table className={classes.table}>
          <colgroup>
            <col style={{ width: "30%" }} />
            <col style={{ width: "8%" }} />
            <col style={{ width: "8%" }} />
            <col style={{ width: "8%" }} />
          </colgroup>
          {renderTableHeader()}
          <TableBody>
            {data?.length === 0 && (
              <TableRow>
                <TableCell align="center" colSpan={4}>
                  <span className={classes.noResults}>No results found</span>
                </TableCell>
              </TableRow>
            )}
            {data?.length > 0 &&
              data.map((row) => {
                total += row.tc;
                return (
                  <TableRow key={row.store}>
                    <TableCell>{row.storeNumber || row.store}</TableCell>
                    <TableCell>{displayDuration(row.otd)}</TableCell>
                    <TableCell>{displayDuration(row.atd, true)}</TableCell>
                    <TableCell>{displayAmount(row.tc)}</TableCell>
                  </TableRow>
                );
              })}
            {data?.length > 0 && (
              <TableRow>
                <TableCell
                  align="left"
                  colSpan={3}
                  className={classes.grandtotal}
                >
                  GRAND TOTAL
                </TableCell>
                <TableCell className={classes.grandtotal}>
                  {displayAmount(total)}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  const renderRiders = () => {
    let total = 0;
    return (
      <TableContainer component={Paper}>
        <Table className={classes.table}>
          <colgroup>
            <col style={{ width: "30%" }} />
            <col style={{ width: "8%" }} />
            <col style={{ width: "8%" }} />
            <col style={{ width: "8%" }} />
          </colgroup>
          {renderTableHeader()}
          <TableBody>
            {data?.length === 0 && (
              <TableRow>
                <TableCell align="center" colSpan={4}>
                  <span className={classes.noResults}>No results found</span>
                </TableCell>
              </TableRow>
            )}
            {data?.length > 0 &&
              data.map((row) => {
                total += row.tc;

                return (
                  <React.Fragment
                    key={`header-${row.store
                      .replace("@", "")
                      .replace(/\./g, "")}`}
                  >
                    <TableRow className={classes.storeHeader}>
                      <TableCell className={classes.header}>
                        {row.storeNumber || row.store}
                      </TableCell>
                      <TableCell className={classes.header}>
                        {displayDuration(row.otd, true)}
                      </TableCell>
                      <TableCell className={classes.header}>
                        {displayDuration(row.atd, true)}
                      </TableCell>
                      <TableCell className={classes.header}>
                        {displayAmount(row.tc)}
                      </TableCell>
                    </TableRow>
                    {row.rider?.length > 0 && (
                      <React.Fragment key={`${row.storeNumber}_1`}>
                        <TableRow>
                          <TableCell
                            colSpan={4}
                            className={classes.riderHeader}
                          >
                            Riders
                          </TableCell>
                        </TableRow>
                        {row.rider.map((riderRow) => (
                          <TableRow key={riderRow.name}>
                            <TableCell>{riderRow.name}</TableCell>
                            <TableCell>
                              {displayDuration(riderRow.otd, true)}
                            </TableCell>
                            <TableCell>
                              {displayDuration(riderRow.atd, true)}
                            </TableCell>
                            <TableCell>{displayAmount(riderRow.tc)}</TableCell>
                          </TableRow>
                        ))}
                      </React.Fragment>
                    )}
                  </React.Fragment>
                );
              })}
            {data?.length > 0 && (
              <TableRow>
                <TableCell
                  align="left"
                  colSpan={3}
                  className={classes.grandtotal}
                >
                  GRAND TOTAL
                </TableCell>
                <TableCell className={classes.grandtotal}>
                  {displayAmount(total)}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  return (
    <Loader loaded={data !== null}>
      <Typography variant="h5" gutterBottom>
        FYF Tracker
      </Typography>

      <DateFilter
        startDate={startDate}
        endDate={endDate}
        setStartDate={setStartDate}
        setEndDate={setEndDate}
        filterAction={fetchData}
        exportComponent={{
          component: Export,
          data,
          filename: "fyf-report",
          disabled: data?.length === 0,
        }}
      />

      <div className={classes.root}>
        <Grid container spacing={3}>
          <Grid item xs={6}>
            <Paper className={classes.paper}>{renderStores()}</Paper>
          </Grid>
          <Grid item xs={6}>
            <Paper className={classes.paper}>{renderRiders()}</Paper>
          </Grid>
        </Grid>
      </div>
    </Loader>
  );
}

export default MainLayout(FYFReport);
